import {
  Button,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  makeStyles
} from '@material-ui/core';
import { cleanDate, formatDate } from 'src/utils';
import React, { FC, memo, useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { TapTapStatusReport } from 'src/redux/slices/taptap-customer/types';
import { TaptapEodModal } from 'src/components/modal/TaptapEodModal';

const useStyles = makeStyles({
  tableContainer: {
    marginTop: 16,
    border: '1px solid black',
    height: '33vh'
  },
  table: {
    width: '100%',
    minWidth: 800,
    tableLayout: 'fixed'
  },
  tableCell: {
    padding: 8,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    zIndex: 0
  },
  tableHeaderCell: {
    '& .MuiTableCell-head': {
      textAlign: 'center',
      fontWeight: 'bold',
      position: 'sticky'
    }
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#e0e0e0'
    },
    '&:nth-of-type(even)': {
      backgroundColor: '#ffffff'
    },
    '&:hover': {
      background: '#bdbdbd',
      '& $stickyColumn': {
        background: '#bdbdbd'
      }
    }
  },
  stickyColumn: {
    position: 'sticky',
    left: 0,
    zIndex: 11,
    backgroundColor: '#f0f0f0',
    border: '1px solid black',
    '&:hover, $tableRow:hover &': {
      background: '#bdbdbd'
    }
  },
  col: { width: 133 }
});

interface Props {
  reports?: TapTapStatusReport[];
  isLoading: boolean;
}

type GroupedReportsDictionary = { [key: string]: TapTapStatusReport[] };

const component: FC<Props> = ({ reports, isLoading }) => {
  const classes = useStyles();

  const [activeModalKey, setActiveModalKey] = useState<string | null>(null);
  const [currentStatus, setCurrentStatus] = useState<string>('');
  const [currentRecords, setCurrenRecords] = useState<TapTapStatusReport[]>([]);
  const [currentBranch, setCurrentBranch] = useState<string>('');

  const uniqueDates = Array.from(
    new Set(reports?.map((records) => cleanDate(records.created_at ?? '')))
  );
  const branches = Array.from(
    new Set(reports?.map((records) => records.branch_release ?? ''))
  );

  const groupedReports = (reports || []).reduce<GroupedReportsDictionary>(
    (acc, item) => {
      const itemDate = cleanDate(item.created_at ?? '');
      const branch = item.branch_release ?? 'NO_BRANCH';
      const groupKey = `${itemDate}_${branch}`;

      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(item);
      return acc;
    },
    {}
  );

  return (
    <PerfectScrollbar>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table stickyHeader className={classes.table}>
          <TableHead style={{ textAlign: 'center' }}>
            <TableRow className={`${classes.tableHeaderCell}`}>
              <TableCell className={classes.col}>Report Date</TableCell>
              <TableCell className={classes.col}>Total Completed</TableCell>
              <TableCell className={classes.col}>Total On Hold</TableCell>
              <TableCell className={classes.col}>Total Ongoing</TableCell>
              <TableCell className={classes.col}>Total Pending</TableCell>
              <TableCell className={classes.col}>Branch</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : reports?.length ? (
              uniqueDates.flatMap((date) =>
                branches.map((branch) => {
                  const groupKey = `${date}_${branch || 'NO_BRANCH'}`;
                  const groupItems = groupedReports[groupKey];

                  if (!groupItems || groupItems.length === 0) return null;

                  const statusCompleted = groupItems.filter(
                    (item) => item.status?.toUpperCase() === 'COMPLETED'
                  );

                  const statusOnHold = groupItems.filter((item) =>
                    ['HOLD', 'CANCELLED'].includes(
                      item.status?.toUpperCase() ?? ''
                    )
                  );

                  const statusOngoing = groupItems.filter((item) =>
                    [
                      'BUILDING',
                      'ONGOING DELIVERY',
                      'TESTING',
                      'READY FOR DELIVERY',
                      'READY FOR PICKUP'
                    ].includes(item.status?.toUpperCase() ?? '')
                  );

                  const statusPending = groupItems.filter((item) =>
                    [
                      'FOR PAYMENT',
                      'FOR FULLPAYMENT',
                      'FORCHECKING',
                      'FOR PACK',
                      'PREPARING ITEMS',
                      'ON QUEUE',
                      'DONE PAYMENT'
                    ].includes(item.status?.toUpperCase() ?? '')
                  );

                  return (
                    <TableRow key={groupKey} className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>
                        {formatDate(date)}
                      </TableCell>

                      <TableCell className={classes.tableCell}>
                        {statusCompleted.length === 0 ? (
                          statusCompleted.length
                        ) : (
                          <Button
                            onClick={() => {
                              setActiveModalKey(groupKey);
                              setCurrentStatus('Completed');
                              setCurrenRecords(statusCompleted);
                              setCurrentBranch(branch);
                            }}
                          >
                            View ({statusCompleted.length}){' '}
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {statusOnHold.length === 0 ? (
                          statusOnHold.length
                        ) : (
                          <Button
                            onClick={() => {
                              setActiveModalKey(groupKey);
                              setCurrentStatus('On Hold');
                              setCurrenRecords(statusOnHold);
                              setCurrentBranch(branch);
                            }}
                          >
                            View ({statusOnHold.length}){' '}
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {statusOngoing.length === 0 ? (
                          statusOngoing.length
                        ) : (
                          <Button
                            onClick={() => {
                              setActiveModalKey(groupKey);
                              setCurrentStatus('Ongoing');
                              setCurrenRecords(statusOngoing);
                              setCurrentBranch(branch);
                            }}
                          >
                            View ({statusOngoing.length}){' '}
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {statusPending.length === 0 ? (
                          statusPending.length
                        ) : (
                          <Button
                            onClick={() => {
                              setActiveModalKey(groupKey);
                              setCurrentStatus('Pending');
                              setCurrenRecords(statusPending);
                              setCurrentBranch(branch);
                            }}
                          >
                            View ({statusPending.length}){' '}
                          </Button>
                        )}
                      </TableCell>

                      <TableCell className={classes.tableCell}>
                        {branch || 'N/A'}
                      </TableCell>
                      <TaptapEodModal
                        record={currentRecords}
                        visible={activeModalKey === groupKey}
                        onHandleClose={() => setActiveModalKey(null)}
                        date={date}
                        branch={currentBranch}
                        status={currentStatus}
                      />
                    </TableRow>
                  );
                })
              )
            ) : (
              <TableRow className={classes.tableRow}>
                <TableCell className={classes.tableCell} colSpan={6}>
                  No data found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </PerfectScrollbar>
  );
};

export const TaptapReportTable = memo(component);
